import { z } from 'zod'
import axiosNode from '@/axios-instance-node'
import {
	type HttpCreateRoadmapActivityPayload,
	type HttpUpdateRoadmapActivityPayload,
	RoadmapActivity,
	RoadmapPermissions
} from '@/features/roadmaps/types'
import { parseResponseData } from '@/helpers/utils'

/** Whether the logged-in user may create, edit and delete activities. */
export const httpGetRoadmapPermissions =
	async (): Promise<RoadmapPermissions> => {
		const response = await axiosNode.get('v3/roadmap/permissions')
		return parseResponseData({
			responseData: response.data,
			responseShape: RoadmapPermissions
		})
	}

/**
 * Activities overlapping the window, not only those contained by it: the API
 * returns anything whose span touches the range, so a bar running across the
 * whole year still arrives for a single-month window.
 */
export const httpGetRoadmapActivities = async (params: {
	dateFrom: string
	dateTo: string
}): Promise<RoadmapActivity[]> => {
	const response = await axiosNode.get('v3/roadmap', { params })
	return parseResponseData({
		responseData: response.data,
		responseShape: z.array(RoadmapActivity)
	})
}

export const httpCreateRoadmapActivity = async (
	payload: HttpCreateRoadmapActivityPayload
): Promise<RoadmapActivity> => {
	const response = await axiosNode.post('v3/roadmap', payload)
	return parseResponseData({
		responseData: response.data,
		responseShape: RoadmapActivity
	})
}

/** Located by the current title in the body, then `changes` are applied. */
export const httpUpdateRoadmapActivity = async (
	payload: HttpUpdateRoadmapActivityPayload
): Promise<RoadmapActivity> => {
	const response = await axiosNode.patch('v3/roadmap', payload)
	return parseResponseData({
		responseData: response.data,
		responseShape: RoadmapActivity
	})
}

/** Located by title in the body. Returns 204 with no body, nothing to parse. */
export const httpDeleteRoadmapActivity = async (
	title: string
): Promise<void> => {
	await axiosNode.delete('v3/roadmap', { data: { title } })
}
